import {
    QueryClient,
    type QueryKey,
    type UseBaseQueryOptions,
    type UseQueryResult,
    type UseSuspenseQueryResult,
    useQuery,
    useSuspenseQuery
} from "@tanstack/react-query"
import { AxiosError } from "axios"
import isEmpty from "lodash-es/isEmpty"
import isUndefined from "lodash-es/isUndefined"

import { getCurrentAccessToken } from "$/utils/tokens"

import { participantByIdQueryAdapter, userQueryAdapter } from "@/3514/adapters"
import { EParticipantByIdQueryKey, EUserQueryKey } from "@/3514/config"
import { participantByIdQueryFetcher, userQueryFetcher } from "@/3514/fetchers"
import type { IUserDto, IUserModel, IUserParticipantDto, IUserParticipantModel } from "@/3514/types"
import { hours, minutes, seconds } from "@/3514/utils"

// user --start
type TUserQuery = UseQueryResult<IUserModel, AxiosError>
function useUserQuery(): TUserQuery {
    return useQuery<IUserDto, AxiosError, IUserModel, QueryKey>({
        queryKey: EUserQueryKey(),
        queryFn: userQueryFetcher,
        enabled: !isEmpty(getCurrentAccessToken()),
        select: userQueryAdapter,
        notifyOnChangeProps: ["data", "error", "refetch", "isSuccess", "isFetched"],
        refetchInterval: hours(1),
        retry: 1,
        retryDelay: seconds(2),
        refetchIntervalInBackground: true,
        staleTime: hours(1),
        gcTime: hours(1),
        networkMode: "offlineFirst",
        throwOnError: true
    })
}
// user --end

// participant by id --start
type _TParticipantByIdQueryOptions = Pick<UseBaseQueryOptions, "staleTime" | "gcTime" | "enabled"> & {
    userId: number
}

type TParticipantByIdSuspenseQuery = UseSuspenseQueryResult<IUserParticipantModel, AxiosError>
function useParticipantByIdSuspenseQuery({
    userId,
    staleTime,
    gcTime
}: _TParticipantByIdQueryOptions): ReturnType<
    (options: _TParticipantByIdQueryOptions) => TParticipantByIdSuspenseQuery
> {
    return useSuspenseQuery<IUserParticipantDto, AxiosError, IUserParticipantModel, QueryKey>({
        queryKey: EParticipantByIdQueryKey(userId),
        queryFn: participantByIdQueryFetcher,
        select: participantByIdQueryAdapter,
        staleTime: isUndefined(staleTime) ? minutes(30) : staleTime,
        gcTime: isUndefined(gcTime) ? minutes(15) : gcTime,
        notifyOnChangeProps: ["data", "isSuccess"],
        refetchIntervalInBackground: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        networkMode: "online"
    })
}

type TParticipantByIdQuery = UseQueryResult<IUserParticipantModel, AxiosError>
function useParticipantByIdQuery({
    userId,
    staleTime,
    gcTime,
    enabled = true
}: _TParticipantByIdQueryOptions): ReturnType<(options: _TParticipantByIdQueryOptions) => TParticipantByIdQuery> {
    return useQuery<IUserParticipantDto, AxiosError, IUserParticipantModel, QueryKey>({
        queryKey: EParticipantByIdQueryKey(userId),
        queryFn: participantByIdQueryFetcher,
        select: participantByIdQueryAdapter,
        staleTime: isUndefined(staleTime) ? minutes(30) : staleTime,
        gcTime: isUndefined(gcTime) ? minutes(15) : gcTime,
        notifyOnChangeProps: ["data", "dataUpdatedAt", "isError", "error", "isFetching", "isSuccess"],
        refetchIntervalInBackground: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        networkMode: "online",
        enabled,
        throwOnError: false
    })
}

async function participantByIdQueryPrefetcher(
    { qc }: { qc: QueryClient },
    { participantId }: { participantId: number },
    queryOptions?: { staleTime: number }
): Promise<void> {
    await qc.prefetchQuery<IUserParticipantDto, AxiosError, IUserParticipantModel, QueryKey>({
        queryKey: EParticipantByIdQueryKey(participantId),
        queryFn: participantByIdQueryFetcher,
        staleTime: queryOptions?.staleTime || minutes(30),
        gcTime: queryOptions?.staleTime ? queryOptions.staleTime / 2 : minutes(15)
    })
}

export { useUserQuery, useParticipantByIdSuspenseQuery, useParticipantByIdQuery, participantByIdQueryPrefetcher }
export type { TUserQuery, TParticipantByIdSuspenseQuery, TParticipantByIdQuery }
